import { Injectable } from '@angular/core';
import {
  ReconciliationComparisonRequest,
  ReconciliationComparisonResult,
  ReconciliationGetListResponse,
  ReconciliationListItem,
  ReconciliationPeriodsResponse,
  ReconciliationRawGetListResponse,
  ReconciliationSettingsResponse,
} from '@ag-common-lib/public-api';
import { LoadOptions } from 'devextreme/common/data';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { ReportingApiService } from './reporting-api.service';

const RECONCILIATION_GRID_PAGE_SIZE = 50;
const RECONCILIATION_LIST_DEFAULT_SORT = [{ selector: 'reportingPeriod', desc: true }];
const RECONCILIATION_DETAIL_LIST_DEFAULT_SORT = [{ selector: 'week', desc: false }];

@Injectable()
export class ReconciliationApiService {
  private readonly basePath = '/api/reconciliation';
  private readonly gridDataSource = new DataSource({
    paginate: true,
    pageSize: RECONCILIATION_GRID_PAGE_SIZE,
    store: new CustomStore({
      key: ['year', 'week'],
      load: async (loadOptions: LoadOptions): Promise<ReconciliationGetListResponse> => {
        console.log('ReconciliationApiService loadOptions', loadOptions);
        return this.getList(loadOptions);
      },
    }),
  });

  constructor(private readonly reportingApiService: ReportingApiService) {}

  getDataSource(): DataSource<ReconciliationListItem, string> {
    return this.gridDataSource;
  }

  reloadDataSource(): Promise<void> {
    return this.gridDataSource.reload().then(() => undefined);
  }

  getSettings(): Promise<ReconciliationSettingsResponse> {
    return this.reportingApiService.request<ReconciliationSettingsResponse>(`${this.basePath}/settings`, {
      method: 'GET',
    });
  }

  /** Every period the comparison can be opened for, newest first. */
  getPeriods(): Promise<ReconciliationPeriodsResponse> {
    return this.reportingApiService.request<ReconciliationPeriodsResponse>(`${this.basePath}/periods`, {
      method: 'GET',
    });
  }

  getList(loadOptions: LoadOptions | Record<string, unknown> = {}): Promise<ReconciliationGetListResponse> {
    return this.reportingApiService.request<ReconciliationGetListResponse>(`${this.basePath}/get-list`, {
      method: 'POST',
      body: this.normalizeLoadOptions(loadOptions, RECONCILIATION_LIST_DEFAULT_SORT),
    });
  }

  getDetailList(
    year: number,
    week: number,
    loadOptions: LoadOptions | Record<string, unknown> = {},
  ): Promise<ReconciliationRawGetListResponse> {
    return this.reportingApiService.request<ReconciliationRawGetListResponse>(
      `${this.basePath}/${year}/${week}/get-list`,
      {
        method: 'POST',
        body: this.normalizeLoadOptions(loadOptions, RECONCILIATION_DETAIL_LIST_DEFAULT_SORT),
      },
    );
  }

  getComparison(request: ReconciliationComparisonRequest): Promise<ReconciliationComparisonResult> {
    return this.reportingApiService.request<ReconciliationComparisonResult>(`${this.basePath}/comparison/get`, {
      method: 'POST',
      body: request,
    });
  }

  getComparisonPdf(request: ReconciliationComparisonRequest): Promise<Blob> {
    return this.reportingApiService.request<Blob>(`${this.basePath}/comparison/pdf`, {
      method: 'POST',
      body: request,
      responseType: 'blob',
    });
  }

  refreshByYear(year: number): Promise<void> {
    return this.reportingApiService.request<void>(`${this.basePath}/refresh-by-year/${year}`, {
      method: 'POST',
    });
  }

  private normalizeLoadOptions(
    loadOptions: LoadOptions | Record<string, unknown> | null | undefined,
    defaultSort: Array<{ selector: string; desc: boolean }>,
  ): Record<string, unknown> {
    if (!loadOptions || typeof loadOptions !== 'object') {
      return {
        skip: 0,
        take: RECONCILIATION_GRID_PAGE_SIZE,
        requireTotalCount: true,
        sort: defaultSort,
      };
    }

    const normalizedLoadOptions = Object.entries(loadOptions as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((result, [key, value]) => {
      if (value === undefined) {
        return result;
      }

      result[key] = value;
      return result;
    }, {});

    normalizedLoadOptions.requireTotalCount = true;
    normalizedLoadOptions['skip'] ??= 0;
    normalizedLoadOptions['take'] ??= RECONCILIATION_GRID_PAGE_SIZE;

    if (!normalizedLoadOptions['sort']) {
      normalizedLoadOptions['sort'] = defaultSort;
    }

    return normalizedLoadOptions;
  }
}
